import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosRequestConfig } from "axios";
import { CANCELLED_REASONS, NOT_LOADED_STATE } from "src/common/ApiCall";
import { PEV } from "src/common/utils";
import * as service from "../service";
import { RequestBodyForCurrentAccessControl } from "./requestBodyForCurrentAccessControl";
import { RequestBodyForCurrentAccessControlBasedOnInformationAssets } from "./requestBodyForCurrentAccessControlBasedOnInformationAssets";

const initialState = {
  systemResourceTypes: PEV(
    [],
    //@ts-ignore
    NOT_LOADED_STATE
  ),
  currentAccessControlsBasedOnSystemResources: PEV(
    {
      currentAccessControls: [],
      totalCount: 0,
      allRegions: [],
      allCountries: [],
      userGroups: [],
    },
    //@ts-ignore
    NOT_LOADED_STATE
  ),
  currentAccessControlsBasedOnInformationAssets: PEV(
    { currentAccessControls: [], totalCount: 0, userGroups: [] },
    //@ts-ignore
    NOT_LOADED_STATE
  ),
};

const getSystemResourceTypes = createAsyncThunk("systemResourceType/get", async () => {
  return await service.getSystemResourceTypes();
});

const getCurrentAccessControlsBasedOnSystemResource = createAsyncThunk(
  "currentAccessControlsBasedOnSystemResource/get",
  async (arg: {
    requestBody: RequestBodyForCurrentAccessControl;
    axiosConfig: AxiosRequestConfig;
  }) => {
    return await service.getCurrentAccessControlsBasedOnSystemResource(
      arg.requestBody,
      arg.axiosConfig
    );
  }
);

const getCurrentAccessControlsBasedOnInformationAssets = createAsyncThunk(
  "currentAccessControlsBasedOnInformationAsset/get",
  async (arg: {
    requestBody: RequestBodyForCurrentAccessControlBasedOnInformationAssets;
    axiosConfig: AxiosRequestConfig;
  }) => {
    return await service.getCurrentAccessControlsBasedOnInformationAssets(
      arg.requestBody,
      arg.axiosConfig
    );
  }
);

const AccessVisibilitySlice = createSlice({
  name: "featureToggleList",
  initialState,
  reducers: {
    reset(state) {
      state.currentAccessControlsBasedOnSystemResources =
        initialState.currentAccessControlsBasedOnSystemResources;
      state.systemResourceTypes = initialState.systemResourceTypes;
    },
  },
  extraReducers: {
    [getSystemResourceTypes.pending.type]: (state) => {
      state.systemResourceTypes = PEV(
        initialState.systemResourceTypes.value,
        //@ts-ignore
        "Fetching system resource types"
      );
    },
    [getSystemResourceTypes.fulfilled.type]: (state, action) => {
      state.systemResourceTypes = PEV(action.payload);
    },
    [getSystemResourceTypes.rejected.type]: (state) => {
      state.systemResourceTypes = PEV(
        initialState.systemResourceTypes.value,
        null,
        //@ts-ignore
        "Error while fetching system resource types"
      );
    },
    [getCurrentAccessControlsBasedOnSystemResource.pending.type]: (state) => {
      state.currentAccessControlsBasedOnSystemResources = PEV(
        initialState.currentAccessControlsBasedOnSystemResources.value,
        //@ts-ignore
        "Fetching current access controls based on system resource"
      );
    },
    [getCurrentAccessControlsBasedOnSystemResource.fulfilled.type]: (state, action) => {
      state.currentAccessControlsBasedOnSystemResources = PEV(action.payload);
    },
    [getCurrentAccessControlsBasedOnSystemResource.rejected.type]: (state, action) => {
      if (action.error.message === CANCELLED_REASONS.CalledApiAgain) {
        state.currentAccessControlsBasedOnSystemResources = PEV(
          initialState.currentAccessControlsBasedOnSystemResources.value,
          //@ts-ignore
          "Fetching current access controls based on system resource"
        );
      } else {
        state.currentAccessControlsBasedOnSystemResources = PEV(
          initialState.currentAccessControlsBasedOnSystemResources.value,
          null,
          //@ts-ignore
          "Error while fetching current access controls based on system resource"
        );
      }
    },
    [getCurrentAccessControlsBasedOnInformationAssets.pending.type]: (state) => {
      state.currentAccessControlsBasedOnInformationAssets = PEV(
        initialState.currentAccessControlsBasedOnInformationAssets.value,
        //@ts-ignore
        "Fetching current access controls based on information asset"
      );
    },
    [getCurrentAccessControlsBasedOnInformationAssets.fulfilled.type]: (state, action) => {
      state.currentAccessControlsBasedOnInformationAssets = PEV(action.payload);
    },
    [getCurrentAccessControlsBasedOnInformationAssets.rejected.type]: (state, action) => {
      if (action.error.message === CANCELLED_REASONS.CalledApiAgain) {
        state.currentAccessControlsBasedOnInformationAssets = PEV(
          initialState.currentAccessControlsBasedOnInformationAssets.value,
          //@ts-ignore
          "Fetching current access controls based on information asset"
        );
      } else {
        state.currentAccessControlsBasedOnInformationAssets = PEV(
          initialState.currentAccessControlsBasedOnInformationAssets.value,
          null,
          //@ts-ignore
          "Error while fetching current access controls based on information asset"
        );
      }
    },
  },
});

export const AccessVisibilityReducer = AccessVisibilitySlice.reducer;

const { reset } = AccessVisibilitySlice.actions;

export const AccessVisibilityActions = {
  getSystemResourceTypes,
  getCurrentAccessControlsBasedOnSystemResource,
  getCurrentAccessControlsBasedOnInformationAssets,
  reset,
};
